import {
  BookmarkIcon,
  CommitIcon,
  DotsHorizontalIcon,
  OpenInNewWindowIcon,
  PlusIcon,
  VercelLogoIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import * as React from 'react'
import { toast } from 'sonner'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useToggleState } from '~/hooks/use-toggle-state'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { DocActionDialog } from '~/sotano-kit/primitives/confirmation-dialog'
import { Input } from '~/sotano-kit/primitives/input'
import { Contributors } from './contributors'
import { RepositoryFooter } from './footer'
import { getRepoMenuActions } from './menu'

import type {
  Repository as RepositoryType,
  Team,
} from '@basehub/data/replicache/index'

import { useRouter } from 'next/navigation'
import {
  SimpleContextMenu,
  SimpleDropdownMenu,
} from '~/sotano-kit/primitives/menu-actions-definitions'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/sotano-kit/primitives/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'
import { useUserTeamsList } from '~/hooks-data/team/use-user-teams-list'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useMutation } from '@tanstack/react-query'
import {
  repoTransferInputSchema,
  repoTransferOutputSchema,
} from '../../../../../../api/teams/[team-id]/transfer-repository-request/input-schema'
import { useMyMembershipInTeam } from '~/hooks-data/use-my-membership'
import {
  PublicRepo,
  PublicTeam,
  isForkableRepo,
  isPublicTeam,
  isUserRepo,
  isPublicRepo,
} from '@basehub/data/replicache/mutators/_public-types'
import { ForkDialog } from '~/app/(dash)/[team-slug]/header/fork-dialog'
import { normalizeError } from '@basehub/utils/error'
import Link from 'next/link'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { ForkIcon } from './fork-icon'
import { Bookmark } from '~/app/(dash)/[team-slug]/header/bookmark-action'
import {
  SimpleTooltip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'
import { hasAccessToRepo } from '@basehub/data/replicache/mutators/team-space/team/auth'
import { useBookmark } from '~/hooks-data/use-bookmark'
import { useUserStore } from '~/app/_store/user-store'
import { useLastVisitedBranch } from '~/app/(dash)/[team-slug]/header/branch-switcher'
import { useForkOrigin } from '~/hooks-data/use-fork-origin'

type RepositoryProps = {
  repository: RepositoryType | PublicRepo
  isLoading?: boolean
  handleRename: (
    repository: Pick<RepositoryType, 'title' | 'id'>
  ) => Promise<void>
  handlePrivacyToggle: () => Promise<void>
  handleDelete: () => void
  staticTeamData?: PublicTeam | Team
} & Omit<React.JSX.IntrinsicElements['a'], 'href'>

export const Repository = React.forwardRef<HTMLDivElement, RepositoryProps>(
  (
    {
      repository,
      isLoading,
      handleRename,
      handleDelete,
      handlePrivacyToggle,
      staticTeamData,
    },
    ref
  ) => {
    const menuToggleState = useToggleState()
    const renameToggleState = useToggleState()
    const deleteToggleState = useToggleState()
    const privacyToggleState = useToggleState()
    const transferToggleState = useToggleState()
    const duplicateToggleState = useToggleState()
    const forkToggleState = useToggleState()
    const [inputTitle, setInputTitle] = React.useState(repository.title)
    const renameInputRef = React.useRef<HTMLInputElement>(null)
    const { teamSlug } = useDashboardSlugs()
    const router = useRouter()
    const { team } = useSelectedTeam(staticTeamData)
    const optimisticUser = useUserStore((s) => s.optimisticUser)
    const membership = useMyMembershipInTeam(team)
    const [lastVisitedBranch] = useLastVisitedBranch(repository.slug)
    const { isBookmarked, handleToggleBookmark } = useBookmark({
      team: {
        id: team?.id,
        slug: team?.slug,
      },
      repo: {
        id: repository.id,
        slug: repository.slug,
      },
    })
    const isGuest =
      !membership &&
      repository.guests?.list.find(
        (guest) => guest.userId === optimisticUser?.id
      )

    React.useEffect(() => {
      if (!renameToggleState.isOn) return

      const input = renameInputRef.current

      if (input) {
        input.readOnly = true

        setTimeout(() => {
          renameInputRef.current?.select()
          input.readOnly = false
        }, 100)
      }
    }, [renameToggleState.isOn])

    const menuActions = React.useMemo(
      () =>
        getRepoMenuActions({
          handleRename: () => {
            menuToggleState.handleOff()
            renameToggleState.handleOn()
          },
          handleDelete: () => {
            menuToggleState.handleOff()
            setTimeout(() => {
              deleteToggleState.handleOn()
            }, 1)
          },
          togglePublic: () => {
            menuToggleState.handleOff()
            setTimeout(() => {
              privacyToggleState.handleOn()
            }, 1)
          },
          handleTransfer: () => {
            menuToggleState.handleOff()
            setTimeout(() => {
              transferToggleState.handleOn()
            }, 1)
          },
          handleDuplicate: () => {
            menuToggleState.handleOff()
            setTimeout(() => {
              duplicateToggleState.handleOn()
            }, 1)
          },
          openRepo: () => {
            router.push(`/${teamSlug}/${repository.slug}/explore`)
          },
          handleFork: () => {
            menuToggleState.handleOff()
            forkToggleState.handleOn()
          },
          handleToggleBookmark: () => {
            handleToggleBookmark()
          },
          isBookmarked,
          publicRepo: repository.isPublic ?? false,
          role:
            !isPublicTeam(team) && hasAccessToRepo(membership, repository.id)
              ? (membership?.role ?? 'member')
              : 'viewer',
        }),
      [
        team,
        menuToggleState,
        forkToggleState,
        renameToggleState,
        privacyToggleState,
        deleteToggleState,
        transferToggleState,
        duplicateToggleState,
        router,
        teamSlug,
        repository,
        membership,
        isBookmarked,
        handleToggleBookmark,
      ]
    )

    const isDisabled = React.useMemo(
      () =>
        isLoading ||
        (isUserRepo(repository) && repository.status === 'transferring'),
      [isLoading, repository]
    )
    const { data: forkOrigin } = useForkOrigin(repository, team)

    return (
      <>
        <SimpleContextMenu
          actions={menuActions}
          controls={{
            open: menuToggleState.isOn,
            onOpenChange: menuToggleState.handleToggle,
          }}
        >
          <div
            ref={ref}
            aria-disabled={isDisabled}
            draggable={!renameToggleState.isOn}
            onKeyDown={(e) => {
              if (
                (e.key === 'r' || e.key === 'R') &&
                !e.ctrlKey &&
                !e.metaKey
              ) {
                renameToggleState.handleOn()
              }
            }}
            className={clsx(
              'h-[200px] shadow-repository-card pointer-events-none group relative flex w-full cursor-default flex-col rounded-2xl border border-dark-border-solid bg-dark-bg outline-1 transition-colors duration-200 hover:border-dark-border-solid hover:bg-dark-shade',
              isDisabled && 'opacity-50'
            )}
          >
            <ButtonLink
              unstyled
              className="absolute pointer-events-auto inset-0 rounded-2xl cursor-default"
              href={`/${teamSlug}/${
                repository.slug
              }/explore/${encodeURIComponent(lastVisitedBranch)}`}
            />

            <div className="absolute right-4 top-4 flex gap-x-2">
              {!renameToggleState.isOn && (
                <>
                  {menuActions.length > 1 && !isPublicTeam(team) ? (
                    <SimpleDropdownMenu actions={menuActions}>
                      <Button
                        unstyled
                        focusStyled
                        className="cursor-default rounded-lg border border-dark-border-solid bg-dark-shade light:bg-dark-base-dark light:group-hover:bg-dark-shade light:hover:!bg-dark-shade-hover p-[7px] text-dark-dim outline-1 outline-offset-2 outline-dark-border-solid transition-colors duration-200 hover:border-dark-elevated hover:bg-dark-shade-hover focus:outline pointer-events-auto z-20"
                      >
                        <DotsHorizontalIcon width={16} height={16} />
                      </Button>
                    </SimpleDropdownMenu>
                  ) : team &&
                    isForkableRepo(repository) &&
                    isPublicRepo(repository) ? (
                    <PublicTeamActions
                      team={team}
                      handleForkOn={forkToggleState.handleOn}
                      repository={repository}
                    />
                  ) : null}
                </>
              )}
            </div>

            <div className="flex-1 p-4 flex flex-col transition-colors duration-200 rounded-t-2xl bg-dark-base xl:p-5 group-hover:bg-dark-shade relative z-10">
              <div className="max-w-[90%] space-y-2 flex-grow">
                <p className="flex items-center space-x-1.5 font-medium">
                  <span className="max-w-[calc(100%-32px)] overflow-hidden overflow-ellipsis whitespace-nowrap text-lg leading-[22px]">
                    {repository.title}
                  </span>
                </p>
                {renameToggleState.isOn && (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault()
                      const newTitle = new FormData(e.currentTarget).get(
                        'title'
                      )
                      if (!newTitle || typeof newTitle !== 'string') {
                        return toast.error('Add a title before saving')
                      }
                      await handleRename({ ...repository, title: newTitle })
                      renameToggleState.handleOff()
                    }}
                  >
                    <Input
                      ref={renameInputRef}
                      className="pointer-events-auto absolute left-4 top-4 z-20 !w-[calc(100%-32px)] py-0.5 !px-0.5 !text-lg"
                      type="text"
                      size="sm"
                      name="title"
                      autoFocus
                      value={inputTitle}
                      required
                      onChange={(e) => setInputTitle(e.target.value)}
                      onClick={(e) => e.preventDefault()}
                      onBlur={() => {
                        renameToggleState.handleOff()
                        setInputTitle(repository.title)
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation()
                        if (e.key === 'Escape') {
                          renameToggleState.handleOff()
                          setInputTitle(repository.title)
                        }
                      }}
                    />
                  </form>
                )}
                <div className="h-6">
                  {repository.previewUrl && (
                    <ButtonLink
                      unstyled
                      focusStyled
                      href={repository.previewUrl}
                      className="inline-block hover:underline hover:text-dark-dim truncate line-clamp-1 max-w-full cursor-pointer justify-center items-center leading-normal text-sm pointer-events-auto text-dark-muted"
                      rel="noreferrer"
                      target="_blank"
                    >
                      {repository.previewUrl}
                    </ButtonLink>
                  )}
                </div>
              </div>

              {repository && repository.forkStatus && forkOrigin && (
                <div className="text-xs mb-2 text-dark-muted text-ellipsis overflow-hidden whitespace-nowrap">
                  Forked from{' '}
                  <Link
                    href={`/${forkOrigin.teamSlug}/${forkOrigin.repoSlug}`}
                    className="pointer-events-auto underline hover:text-dark-dim"
                  >
                    {forkOrigin.teamSlug}/{forkOrigin.repoSlug}
                  </Link>
                </div>
              )}
              <div className="h-6 flex gap-x-2 items-center text-xs justify-between">
                <div className="flex gap-x-1 items-center">
                  <span className="px-2 h-[22px] border rounded-5xl flex items-center bg-dark-shade group-hover:bg-dark-shade-hover transition-colors duration-200 border-dark-border-solid text-dark-dim">
                    {repository.isPublic ? 'Public' : 'Private'}
                  </span>
                  {isGuest && (
                    <span className="px-2 h-[22px] border rounded-5xl flex items-center bg-dark-shade group-hover:bg-dark-shade-hover transition-colors duration-200 border-dark-border-solid text-dark-dim">
                      Guest
                    </span>
                  )}
                  {'integrations' in repository &&
                    repository.integrations?.find(
                      (integration) => integration.type === 'vercel'
                    ) && (
                      <SimpleTooltip content="Integrated with Vercel">
                        <span className="pointer-events-auto p-0.5">
                          <VercelLogoIcon className=" text-dark-bright" />
                        </span>
                      </SimpleTooltip>
                    )}
                  {repository.isPublic && (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="px-0.5 flex gap-x-[1px] items-center text-dark-faint">
                            <BookmarkIcon
                              height={15}
                              width={15}
                              className="-mt-px"
                            />
                            {repository.stats?.bookmarks ?? 0}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left" sideOffset={8}>
                          Number of bookmarks
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={clsx(
                              'pointer-events-auto rounded-sm items-center flex gap-x-[1px] px-0.5 text-dark-faint'
                            )}
                          >
                            <ForkIcon
                              height={15}
                              width={15}
                              className="-mt-px"
                            />
                            {repository.stats?.forks ?? 0}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left" sideOffset={8}>
                          Number of forks
                        </TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>

                {team && (
                  <Contributors
                    className="pointer-events-auto"
                    contributorsIds={repository.contributors}
                    team={team}
                    repository={repository}
                    avatarSize={24}
                  />
                )}
              </div>
            </div>
            {isUserRepo(repository) && repository.status === 'transferring' ? (
              <div className="box-content overflow-hidden rounded-b-[15px] border-t border-dark-border-solid bg-gradient-to-tr from-green to-[#16a34a] px-[15px] xl:px-[19px] pt-[11px] pb-3">
                <p className="flex items-center justify-between text-xs leading-4 whitespace-nowrap text-dark-bg">
                  <span>Transferring repository...</span>
                  <OpenInNewWindowIcon />
                </p>
              </div>
            ) : (
              <RepositoryFooter
                latest={
                  repository.latestCommit
                    ? {
                        icon: (
                          <CommitIcon
                            className="flex-shrink-0"
                            width={14}
                            height={14}
                          />
                        ),
                        message: repository.latestCommit.message,
                        at: repository.latestCommit.createdAt,
                      }
                    : {
                        icon: (
                          <PlusIcon
                            className="flex-shrink-0"
                            width={14}
                            height={14}
                          />
                        ),
                        message: 'Repository created',
                        at: repository.createdAt,
                      }
                }
              />
            )}
          </div>
        </SimpleContextMenu>

        <DocActionDialog
          open={deleteToggleState.isOn}
          title={`Are you sure you want to delete the repository: "${repository.title}"?`}
          description="This action can't be undone."
          handleClose={deleteToggleState.handleOff}
          action={handleDelete}
        />
        <DocActionDialog
          intent="primary"
          open={privacyToggleState.isOn}
          title={`You're about to make "${repository.title}" ${
            repository.isPublic ? 'private' : 'public'
          }`}
          description={
            repository.isPublic ? (
              'Making this repository private limits the access to you and the members of your team.'
            ) : (
              <span>
                Making this repository public allows other BaseHub users to{' '}
                <span className="font-medium text-dark-dim">
                  fork, bookmark, and report your repository.
                </span>
              </span>
            )
          }
          handleClose={privacyToggleState.handleOff}
          action={handlePrivacyToggle}
          label={`Make ${repository.isPublic ? 'Private' : 'Public'}`}
        />

        {isUserRepo(repository) && (
          <>
            <TransferOwnershipDialog
              isOn={transferToggleState.isOn}
              onClose={transferToggleState.handleOff}
              repository={repository}
            />

            <DuplicateRepoDialog
              isOn={duplicateToggleState.isOn}
              onClose={duplicateToggleState.handleOff}
              repository={repository}
            />
          </>
        )}

        {team && (
          <ForkDialog
            team={team}
            toggleState={forkToggleState}
            repository={repository}
            hideTrigger
          />
        )}
      </>
    )
  }
)

const TransferOwnershipDialog = ({
  isOn,
  onClose,
  repository,
}: {
  isOn: boolean
  onClose: () => void
  repository: RepositoryType
}) => {
  const {
    data: { teams },
  } = useUserTeamsList()
  const { team: selectedTeam } = useSelectedTeam()
  const router = useRouter()

  const teamsList =
    teams
      ?.filter((t) => t.id !== selectedTeam?.id)
      .sort((a, b) =>
        (a.plan && a.plan === 'personal') || a.plan_v2?.type === 'personal'
          ? -1
          : a.title.localeCompare(b.title)
      ) ?? []

  const [targetTeamId, setTargetTeamId] = React.useState(teamsList[0]?.id ?? '')

  const { mutate, isLoading } = useMutation(
    async ({
      repoIds,
      targetTeamId,
    }: {
      repoIds: Array<RepositoryType['id']>
      targetTeamId: Team['id']
    }) => {
      if (!selectedTeam || !targetTeamId) {
        toast.error('Please select a team to transfer the repository.')
        throw new Error('Please select a team to transfer the repository.')
      }

      const response = await fetch(
        `/api/teams/${selectedTeam?.id}/transfer-repository-request`,
        {
          method: 'POST',
          body: JSON.stringify(
            repoTransferInputSchema.parse({
              repoIds,
              targetTeamId,
            })
          ),
        }
      )

      if (![200, 201].includes(response.status)) {
        const data = await response.json()
        console.error(data)
        const safeError = repoTransferOutputSchema.safeParse(data)
        console.error(safeError)
        if (safeError.success) {
          toast.error(safeError.data.message)
          throw new Error(safeError.data.message)
        }
        toast.error("Unknown error, couldn't transfer repository")
        throw new Error("Unknown error, couldn't transfer repository")
      }

      return response.json()
    }
  )

  return (
    <Dialog open={isOn} onOpenChange={onClose}>
      <DialogContent className="p-6">
        <DialogHeader>
          <DialogTitle>Transfer repository ownership</DialogTitle>
          <DialogDescription>
            Transfer repository from{' '}
            <span className="font-medium text-dark-bright">
              {selectedTeam?.title}
            </span>{' '}
            to another BaseHub team.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1 mt-4">
          <div className="flex items-end justify-between gap-6">
            <div className="relative flex-1">
              <Select value={targetTeamId} onValueChange={setTargetTeamId}>
                <SelectTrigger aria-label="select team">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teamsList.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      <div className="flex !flex-row items-center justify-between gap-2 text-right">
                        <BaseHubAvatar
                          id={team.id}
                          name={team.title}
                          size="xsmall"
                        />
                        <span>{team.title}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="relative">
              <Button intent="secondary" onClick={onClose}>
                Cancel
              </Button>

              <Button
                disabled={isLoading}
                onClick={async () => {
                  mutate(
                    { repoIds: [repository.id], targetTeamId },
                    {
                      onSuccess: () => {
                        onClose()
                        toast.success('Transfer started')
                        // redirect to target team
                        const targetTeam = teams?.find(
                          (team) => team.id === targetTeamId
                        )
                        if (targetTeam) {
                          router.push(`/${targetTeam.slug}`)
                        }
                      },
                    }
                  )
                }}
              >
                Submit
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const DuplicateRepoDialog = ({
  isOn,
  onClose,
  repository,
}: {
  isOn: boolean
  onClose: () => void
  repository: RepositoryType
}) => {
  const { data: teamRep } = useTeamReplicache()
  const {
    data: { teams },
  } = useUserTeamsList()
  const { team: selectedTeam } = useSelectedTeam()
  const [targetTeamId, setTargetTeamId] = React.useState(selectedTeam?.id ?? '')
  const optimisticUser = useUserStore((s) => s.optimisticUser)

  const { mutate, isLoading, error } = useMutation(
    async ({ targetTeamId }: { targetTeamId: Team['id'] }) => {
      if (!selectedTeam || !targetTeamId) {
        toast.error('Please select a team to duplicate the repository.')
        throw new Error('Please select a team to duplicate the repository.')
      }

      const result = await fetch('/api/duplicate-repo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teamSlug: selectedTeam.slug,
          repoSlug: repository.slug,
          targetId: targetTeamId,
          authorId: optimisticUser?.id,
        }),
      })

      if (!result.ok) throw new Error('Could not duplicate repository.')

      await teamRep?.replicache.pull()

      return (await result.json()) as { pathname: string }
    }
  )

  const teamsList =
    teams?.slice().sort((a, b) => a.title.localeCompare(b.title)) ?? []

  return (
    <Dialog open={isOn} onOpenChange={onClose}>
      <DialogContent className="p-5">
        <DialogHeader>
          <DialogTitle>Duplicate repository</DialogTitle>
          <DialogDescription>
            This will create an exact copy of your repository, without
            preserving any connection to the original one.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1 mt-4">
          <div className="flex items-end justify-between gap-6">
            <div className="relative flex-1">
              <Select value={targetTeamId} onValueChange={setTargetTeamId}>
                <SelectTrigger aria-label="select team">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {teamsList.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      <div className="flex !flex-row items-center justify-between gap-2 text-right">
                        <BaseHubAvatar
                          id={team.id}
                          name={team.title}
                          size="xsmall"
                        />
                        <span>{team.title}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button intent="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                disabled={isLoading}
                onClick={async () => {
                  mutate(
                    { targetTeamId },
                    {
                      onSuccess: (data) => {
                        onClose()
                        toast.success(
                          <>
                            Repository duplicated.{' '}
                            <Link
                              href={data?.pathname ?? ''}
                              className="underline transition-colors text-dark-muted hover:text-dark-bright"
                            >
                              Take me there.
                            </Link>
                          </>
                        )
                      },
                    }
                  )
                }}
              >
                Submit
              </Button>
            </DialogFooter>
          </div>
          {!!error && (
            <span className="w-full h-4 text-xs text-dark-error">
              {normalizeError(error).message}
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

const PublicTeamActions = ({
  handleForkOn,
  ...rest
}: {
  team: PublicTeam | Team
  repository: PublicRepo
  handleForkOn: () => void
}) => {
  const handleFork: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    handleForkOn()
  }

  return (
    <div className="flex gap-x-2 z-20 pointer-events-auto">
      <Bookmark
        className="p-[7px] size-4 rounded-lg text-dark-dim outline-1 outline-offset-2 outline-dark-border-solid transition-colors duration-200 focus:outline cursor-default"
        {...rest}
      />
      <Button
        focusStyled
        onClick={handleFork}
        unstyled
        aria-label="Fork repository"
        className="border rounded-lg border-dark-border-solid bg-dark-shade p-[7px] text-dark-dim outline-1 outline-offset-2 outline-dark-border-solid transition-colors duration-200 hover:border-dark-elevated hover:bg-dark-shade-hover focus:outline cursor-default"
      >
        <ForkIcon className="size-4" />
      </Button>
    </div>
  )
}
